import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fab,
  InputAdornment,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as ProcessIcon,
  Receipt as CreditNoteIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import { salesReturnService } from '../../services/salesReturnService'
import {
  SalesReturn,
  SalesReturnStatus,
  SalesReturnReason,
  SALES_RETURN_STATUS_COLORS,
  SALES_RETURN_STATUS_LABELS,
  SALES_RETURN_REASON_COLORS,
  SALES_RETURN_REASON_LABELS,
} from '../../types/salesReturn'
import { usePermissions } from '../../hooks/usePermissions'

// Indian currency formatter
const formatIndianCurrency = (amount: number) => 
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount)

const SalesReturnsPage: React.FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [reasonFilter, setReasonFilter] = useState<string>('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedReturn, setSelectedReturn] = useState<any | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)

  // Fetch sales returns from API
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sales-returns', page, pageSize, searchTerm, statusFilter, reasonFilter],
    queryFn: () => {
      return salesReturnService.getList({
        page,
        per_page: pageSize,
        search_term: searchTerm || undefined,
        status: statusFilter ? [statusFilter] : undefined,
        return_reason: reasonFilter ? [reasonFilter] : undefined,
      })
    }
  })

  const salesReturns = data?.items || []
  const total = data?.total || 0

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, salesReturn: any) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedReturn(salesReturn)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedReturn(null)
  }

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
    setPage(1)
  }

  const handleReasonFilterChange = (event: SelectChangeEvent) => {
    setReasonFilter(event.target.value)
    setPage(1)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    setPage(1)
  }

  const handleViewDetails = () => {
    if (selectedReturn) {
      navigate(`/admin/documents/sales-returns/${selectedReturn.id}`)
    }
    handleMenuClose()
  }

  const handleEdit = () => {
    if (selectedReturn) {
      navigate(`/admin/documents/sales-returns/${selectedReturn.id}/edit`)
    }
    handleMenuClose()
  }

  const handleDownloadPDF = async () => {
    if (selectedReturn) {
      try {
        const blob = await salesReturnService.downloadPDF(selectedReturn.id)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${selectedReturn.return_number}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error downloading PDF:', error)
      }
    }
    handleMenuClose()
  }

  const handleProcessReturn = async () => {
    if (selectedReturn) {
      try {
        await salesReturnService.processReturn(selectedReturn.id)
        enqueueSnackbar(`Return ${selectedReturn.return_number} processed — stock restored`, { variant: 'success' })
        refetch()
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.detail || 'Failed to process return', { variant: 'error' })
      }
    }
    handleMenuClose()
  }

  const handleDownloadCreditNote = async () => {
    if (selectedReturn && selectedReturn.credit_note_number) {
      try {
        const response = await salesReturnService.generateCreditNote(selectedReturn.id)
        // Note: generateCreditNote returns CreditNoteResponse, not a Blob
        // If you need to download a PDF, use a different endpoint
        console.log('Credit note generated:', response)
      } catch (error) {
        console.error('Error downloading credit note:', error)
      }
    }
    handleMenuClose()
  }

  const handleClearFilters = () => {
    setStatusFilter('')
    setReasonFilter('')
    setSearchTerm('')
    setPage(1)
  }

  const hasActiveFilters = statusFilter || reasonFilter

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load sales returns. Please try again later.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Sales Returns
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total} total returns
          </Typography>
        </Box>
        {hasPermission('sales_returns.create') && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
            onClick={() => navigate('/admin/documents/sales-returns/create')}
          >
            Create Return
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search returns..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.values(SalesReturnStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {SALES_RETURN_STATUS_LABELS[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select
                value={reasonFilter}
                label="Reason"
                onChange={handleReasonFilterChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Reasons</MenuItem>
                {Object.values(SalesReturnReason).map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {SALES_RETURN_REASON_LABELS[reason]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant={hasActiveFilters ? "contained" : "outlined"}
                startIcon={<FilterIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography>Loading sales returns...</Typography>
        </Box>
      ) : (
        /* Table View */
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Return #</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Invoice #</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Amount</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Refund</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Credit Note</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salesReturns.map((salesReturn: any) => (
                <TableRow 
                  key={salesReturn.id}
                  hover
                  onClick={() => navigate(`/admin/documents/sales-returns/${salesReturn.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="primary">
                      {salesReturn.return_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {salesReturn.customer?.business_name || salesReturn.customer?.contact_person || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {salesReturn.invoice?.invoice_number || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {format(new Date(salesReturn.return_date), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SALES_RETURN_REASON_LABELS[salesReturn.return_reason as SalesReturnReason] || salesReturn.return_reason}
                      color={SALES_RETURN_REASON_COLORS[salesReturn.return_reason as SalesReturnReason] || 'default'}
                      size="small"
                      sx={{ borderRadius: 4 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="error.main">
                      {formatIndianCurrency(salesReturn.total_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {formatIndianCurrency(salesReturn.refund_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SALES_RETURN_STATUS_LABELS[salesReturn.status as SalesReturnStatus]}
                      color={SALES_RETURN_STATUS_COLORS[salesReturn.status as SalesReturnStatus]}
                      size="small"
                      sx={{ borderRadius: 4 }}
                    />
                  </TableCell>
                  <TableCell>
                    {salesReturn.credit_note_number ? (
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        {salesReturn.credit_note_number}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Not Generated
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="More Actions">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, salesReturn)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {data && total > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ borderRadius: 2 }}
            >
              Previous
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              <Typography variant="body2">
                Page {page} of {Math.ceil(total / pageSize)}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage(page + 1)}
              sx={{ borderRadius: 2 }}
            >
              Next
            </Button>
          </Stack>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 200 }
          }
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {selectedReturn?.status === SalesReturnStatus.DRAFT && hasPermission('sales_returns.update') && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDownloadPDF}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        {selectedReturn?.credit_note_number && (
          <MenuItem onClick={handleDownloadCreditNote}>
            <ListItemIcon><CreditNoteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download Credit Note</ListItemText>
          </MenuItem>
        )}
        {selectedReturn?.status === SalesReturnStatus.APPROVED && hasPermission('sales_returns.process') && (
          <MenuItem onClick={handleProcessReturn}>
            <ListItemIcon><ProcessIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Process Return</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Floating Action Button */}
      {hasPermission('sales_returns.create') && (
        <Fab
          color="secondary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => navigate('/admin/documents/sales-returns/create')}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  )
}

export default SalesReturnsPage
